import {
    Container,
    ContainerRef,
    IPageDraft,
    Page,
    PageDraft
} from "@amzn/ask-legal-domain";
import {
    Alert,
    Badge,
    Box,
    Button,
    ColumnLayout,
    Header,
    Modal,
    RadioGroup,
    RadioGroupProps,
    SpaceBetween,
    Spinner,
    TextContent
} from "@amzn/awsui-components-react";
import * as React from "react";
import { useAPI } from "../../hooks/api-hook";
import { AppContext } from "../../setup/context";

// ─── Types ──────────────────────────────────────────────────────────────

type ContainerStatus = "NEW" | "MODIFIED" | "UNCHANGED";

interface ContainerPublishItem {
    containerRef: ContainerRef;
    title: string;
    status: ContainerStatus;
    action: IPageDraft.ContainerPublishAction;
    sectionLocation: Page.SectionLocation;
    hideAvailable: boolean;
}

export interface SelectivePublishModalProps {
    visible: boolean;
    draftData: PageDraft.Data;
    liveSections: Page.Section[];
    /** Whether live sections are still being fetched from the API */
    liveSectionsLoading?: boolean;
    /** Whether loading live sections failed */
    liveSectionsError?: boolean;
    loading: boolean;
    /** Error message from a failed publish attempt */
    publishError?: string;
    onDismiss: () => void;
    onPublish: (selections: IPageDraft.ContainerPublishSelection[]) => void;
}

// ─── Helpers ────────────────────────────────────────────────────────────

function getStatusBadgeColor(status: ContainerStatus): "green" | "blue" | "grey" {
    switch (status) {
        case "NEW": return "green";
        case "MODIFIED": return "blue";
        case "UNCHANGED": return "grey";
        default: {
            const _exhaustive: never = status;
            return "grey";
        }
    }
}

function getStatusDisplayLabel(status: ContainerStatus): string {
    switch (status) {
        case "NEW": return "New";
        case "MODIFIED": return "Edited";
        case "UNCHANGED": return "No changes";
        default: {
            const _exhaustive: never = status;
            return "Unknown";
        }
    }
}

function getContainerTypeLabel(type: Container.Type): string {
    switch (type) {
        case Container.Type.RICH_TEXT: return "Rich Text";
        case Container.Type.ACCORDION: return "Accordion";
        case Container.Type.ACCORDION_GROUP: return "Accordion Group";
        case Container.Type.DOCUMENT: return "Document";
        case Container.Type.LIST: return "List";
        case Container.Type.DECISION_TREE: return "Decision Tree";
        case Container.Type.WHOS_MY_LAWYER: return "Who's My Lawyer";
        case Container.Type.ADVANCED_LIST: return "Advanced List";
        default: return type;
    }
}

// ─── Styles (extracted to avoid new object references on every render) ──

const containerCardStyles = {
    base: {
        border: "1px solid var(--color-border-divider-default, #e9ebed)",
        borderRadius: "8px",
        padding: "12px 16px",
    } as React.CSSProperties,
    hide: { backgroundColor: "var(--color-background-status-error, #fdf3f1)" } as React.CSSProperties,
    skip: { backgroundColor: "var(--color-background-status-warning, #fefaf0)" } as React.CSSProperties,
    publish: { backgroundColor: "var(--color-background-container-content, #ffffff)" } as React.CSSProperties,
};

function getContainerCardStyle(action: IPageDraft.ContainerPublishAction): React.CSSProperties {
    const variant = action === IPageDraft.ContainerPublishAction.HIDE ? containerCardStyles.hide
        : action === IPageDraft.ContainerPublishAction.SKIP ? containerCardStyles.skip
        : containerCardStyles.publish;
    return { ...containerCardStyles.base, ...variant };
}

// ─── Component ──────────────────────────────────────────────────────────
//
// This component fully unmounts/remounts on each open (parent uses conditional rendering:
// {publishModalVisible && <SelectivePublishModal/>}), so all state resets automatically.
// No refs needed — all data flows through state → render → effect.

export const SelectivePublishModal: React.FC<SelectivePublishModalProps> = (props) => {
    const context = React.useContext(AppContext);
    const [items, setItems] = React.useState<ContainerPublishItem[]>([]);
    const [metadataLoadTriggered, setMetadataLoadTriggered] = React.useState(false);

    // Load container metadata (titles) via batchLoadMeta
    const batchLoadRunner = useAPI(context.getContainerAPI().batchLoadMetadata);

    // Metadata load — fires once on mount (parallel with liveSections fetch).
    // metadataLoaded state prevents re-triggering.
    React.useEffect(() => {
        if (!props.visible || metadataLoadTriggered) return;
        setMetadataLoadTriggered(true);

        const refs = props.draftData.newSections.flatMap(section => section.containers);
        if (refs.length > 0) {
            batchLoadRunner.submitRun(refs);
        }
    }, [props.visible, props.draftData.newSections, metadataLoadTriggered]);

    // Single unified effect: builds items from draft sections + live sections + metadata titles.
    // Re-runs when ANY data source becomes available, eliminating the race condition between
    // liveSections loading and metadata loading. Includes batchLoadRunner in deps so that
    // when metadata arrives after items are already initialized, titles get applied correctly.
    React.useEffect(() => {
        if (!props.visible || props.liveSectionsLoading || props.liveSectionsError) return;

        // Build live-container lookup
        const liveContainerMap = new Map<string, ContainerRef>();
        for (const section of props.liveSections) {
            for (const c of section.containers) {
                liveContainerMap.set(c.id, c);
            }
        }

        // Build title lookup (may still be empty if metadata is in-flight)
        const titleMap = new Map<string, string>();
        if (batchLoadRunner.status === "Succeeded" && batchLoadRunner.data?.output) {
            for (const c of batchLoadRunner.data.output) {
                titleMap.set(c.id, c.title || c.name || c.id);
            }
        }

        const newItems: ContainerPublishItem[] = props.draftData.newSections.flatMap(section =>
            section.containers.map(containerRef => {
                const liveContainer = liveContainerMap.get(containerRef.id);
                const status: ContainerStatus = !liveContainer ? "NEW"
                    : liveContainer.version !== containerRef.version ? "MODIFIED"
                    : "UNCHANGED";
                return {
                    containerRef,
                    title: titleMap.get(containerRef.id)
                        || `Container ${containerRef.id.split("-").pop()}`,
                    status,
                    action: IPageDraft.ContainerPublishAction.PUBLISH,
                    sectionLocation: section.location as Page.SectionLocation,
                    hideAvailable: liveContainerMap.has(containerRef.id),
                };
            })
        );

        // Preserve user-selected actions from previous state
        setItems(prev => {
            if (prev.length === 0) return newItems;
            return newItems.map(item => {
                const existing = prev.find(p => p.containerRef.id === item.containerRef.id);
                return existing ? { ...item, action: existing.action } : item;
            });
        });
    }, [
        props.visible, props.draftData.newSections, props.liveSections,
        props.liveSectionsLoading, props.liveSectionsError,
        batchLoadRunner.status, batchLoadRunner.data,
    ]);

    // Action change handler
    const onActionChange = (containerId: string, newAction: IPageDraft.ContainerPublishAction) => {
        setItems(prev => prev.map(item =>
            item.containerRef.id === containerId
                ? { ...item, action: newAction }
                : item
        ));
    };

    // Compute summary counts in a single pass
    const { publishCount, skipCount, hideCount } = React.useMemo(() => {
        let p = 0, s = 0, h = 0;
        for (const i of items) {
            if (i.action === IPageDraft.ContainerPublishAction.PUBLISH) p++;
            else if (i.action === IPageDraft.ContainerPublishAction.SKIP) s++;
            else if (i.action === IPageDraft.ContainerPublishAction.HIDE) h++;
            else { const _exhaustive: never = i.action; }
        }
        return { publishCount: p, skipCount: s, hideCount: h };
    }, [items]);

    // Sorted section locations for predictable display order
    const sortedLocations = React.useMemo(() => {
        const order: Record<string, number> = { "Main": 0, "Right": 1 };
        return [...new Set(items.map(i => i.sectionLocation))]
            .sort((a, b) => (order[a] ?? 99) - (order[b] ?? 99));
    }, [items]);

    // Handle publish
    const handlePublish = () => {
        const selections = items.map(item =>
            IPageDraft.ContainerPublishSelection.create({
                containerId: item.containerRef.id,
                action: item.action
            })
        );
        props.onPublish(selections);
    };

    // Unified dismiss guard — used by both modal overlay and Cancel button
    const safeDismiss = () => {
        if (!props.loading && !props.liveSectionsLoading) props.onDismiss();
    };

    // Get radio options for a container
    const getRadioOptions = (item: ContainerPublishItem): RadioGroupProps.RadioButtonDefinition[] => {
        const options: RadioGroupProps.RadioButtonDefinition[] = [
            {
                value: IPageDraft.ContainerPublishAction.PUBLISH,
                label: "Publish to live",
                description: "Update the live page with the draft version of this container"
            },
            {
                value: IPageDraft.ContainerPublishAction.SKIP,
                label: "Keep current",
                description: item.status === "NEW"
                    ? "Do not add this container to the live page yet"
                    : "Keep the current live version unchanged; continue editing in draft"
            },
        ];
        if (item.hideAvailable) {
            options.push({
                value: IPageDraft.ContainerPublishAction.HIDE,
                label: "Remove from live",
                description: "Take this container off the live page; edits are preserved in draft"
            });
        }
        return options;
    };

    // Render container items for a section
    const renderSectionContainers = (location: Page.SectionLocation) => {
        const sectionItems = items.filter(i => i.sectionLocation === location);
        if (sectionItems.length === 0) return null;

        return (
            <SpaceBetween direction="vertical" size="xs">
                <Header variant="h3">{location} Section</Header>
                {sectionItems.map(item => (
                    <div key={item.containerRef.id} style={getContainerCardStyle(item.action)}>
                        <SpaceBetween direction="vertical" size="xxs">
                            <Box>
                                <SpaceBetween direction="horizontal" size="xs">
                                    <TextContent>
                                        <strong>{batchLoadRunner.status === "Running" ? <Spinner /> : item.title}</strong>
                                    </TextContent>
                                    <Badge color={getStatusBadgeColor(item.status)}>{getStatusDisplayLabel(item.status)}</Badge>
                                    <Box color="text-body-secondary" fontSize="body-s">
                                        {getContainerTypeLabel(item.containerRef.containerType)}
                                    </Box>
                                </SpaceBetween>
                            </Box>
                            <RadioGroup
                                onChange={({ detail }) =>
                                    onActionChange(item.containerRef.id, detail.value as IPageDraft.ContainerPublishAction)
                                }
                                value={item.action}
                                items={getRadioOptions(item)}
                            />
                        </SpaceBetween>
                    </div>
                ))}
            </SpaceBetween>
        );
    };

    return (
        <Modal
            visible={props.visible}
            onDismiss={safeDismiss}
            header="Publish Page Draft"
            size="large"
            footer={
                <Box float="right">
                    <SpaceBetween direction="horizontal" size="xs">
                        <Button variant="link" onClick={safeDismiss} disabled={props.loading || !!props.liveSectionsLoading}>
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            loading={props.loading}
                            disabled={items.length === 0 || !!props.liveSectionsError || !!props.liveSectionsLoading}
                            onClick={handlePublish}
                        >
                            {publishCount === items.length
                                ? "Publish All"
                                : `Publish Page (${publishCount} container${publishCount !== 1 ? "s" : ""})`
                            }
                        </Button>
                    </SpaceBetween>
                </Box>
            }
        >
            <SpaceBetween direction="vertical" size="m">
                {/* Page metadata summary */}
                <Box>
                    <TextContent>
                        <p><strong>Page Title:</strong> {props.draftData.newPageTitle}</p>
                        {props.draftData.newPageDescription && (
                            <p><strong>Description:</strong> {props.draftData.newPageDescription}</p>
                        )}
                    </TextContent>
                </Box>

                {/* Error state: failed to load live page for comparison */}
                {props.liveSectionsError && (
                    <Alert type="error">
                        Failed to load the live page for comparison. Container statuses may be inaccurate.
                        Publishing is disabled until the live page data can be loaded. Please close and try again.
                    </Alert>
                )}

                {/* Loading state */}
                {props.liveSectionsLoading && (
                    <Box textAlign="center" padding="l">
                        <Spinner /> Loading live page for comparison...
                    </Box>
                )}

                {/* Publish error alert */}
                {props.publishError && (
                    <Alert type="error">
                        {props.publishError}
                    </Alert>
                )}

                {/* Container metadata load error — non-blocking */}
                {batchLoadRunner.status === "Error" && (
                    <Alert type="warning">
                        Could not load container titles. Containers are shown with abbreviated IDs instead.
                    </Alert>
                )}

                {/* Container selections by section — hidden during loading or error */}
                {!props.liveSectionsError && !props.liveSectionsLoading && sortedLocations.map(loc => (
                    <React.Fragment key={loc}>
                        {renderSectionContainers(loc)}
                    </React.Fragment>
                ))}

                {/* Summary — hidden when no items loaded yet */}
                {!props.liveSectionsLoading && !props.liveSectionsError && items.length > 0 && <Alert type={skipCount + hideCount > 0 ? "info" : "success"}>
                    <ColumnLayout columns={3}>
                        <TextContent>
                            <span style={{ color: "var(--color-text-status-success, #037f0c)" }}>🟢 Publish to live: {publishCount}</span>
                        </TextContent>
                        <TextContent>
                            <span style={{ color: "var(--color-text-status-warning, #8d6605)" }}>🟡 Keep current: {skipCount}</span>
                        </TextContent>
                        <TextContent>
                            <span style={{ color: "var(--color-text-status-error, #d91515)" }}>🔴 Remove from live: {hideCount}</span>
                        </TextContent>
                    </ColumnLayout>
                </Alert>}

                {/* Continuation draft explanation */}
                {!props.liveSectionsError && (skipCount > 0 || hideCount > 0) && (
                    <Alert type="warning">
                        <SpaceBetween direction="vertical" size="xxs">
                            <TextContent>
                                <strong>A new draft will be automatically created after publishing.</strong>
                            </TextContent>
                            <TextContent>
                                <small>
                                    {skipCount > 0 && hideCount > 0 && (
                                        <>The {skipCount} kept-current and {hideCount} removed container{skipCount + hideCount > 1 ? "s" : ""} will be preserved in a new draft so you can continue editing them. </>
                                    )}
                                    {skipCount > 0 && hideCount === 0 && (
                                        <>The {skipCount} kept-current container{skipCount > 1 ? "s" : ""} will be preserved in a new draft so you can continue editing and publish {skipCount > 1 ? "them" : "it"} later. </>
                                    )}
                                    {skipCount === 0 && hideCount > 0 && (
                                        <>The {hideCount} removed container{hideCount > 1 ? "s" : ""} will be taken off the live page but preserved in a new draft. You can re-publish {hideCount > 1 ? "them" : "it"} when ready. </>
                                    )}
                                    You can find this draft in the Draft tab of the page admin view.
                                    Page title and description are always published regardless of container selections.
                                    You may also publish with all containers set to "Keep current" or "Remove" to update only the page title and description.
                                </small>
                            </TextContent>
                        </SpaceBetween>
                    </Alert>
                )}
            </SpaceBetween>
        </Modal>
    );
};
